// What another part adds to a document body (the ui fence runtime is one):
// a node view for code blocks of its language, items of the `/` menu and the
// block menu, how the static view draws its blocks, and what the switch from
// the static view to the editor waits for.
//
// Hosts list them in `documentsScreen({ blocks })`; the document screen hands
// them down through `DocumentBlockPartsContext`. Only the document body takes
// them: a note field (a Case's context) and the footnote dialog keep plain
// code blocks, so a ```ui typed there stays code. Leaving a part out of the
// list turns it off everywhere.
import type { Editor, NodeViewRenderer } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { type ComponentType, createContext, useContext } from "react";
import type { UiAnswersSource } from "../../ui-fence/answers";
import type { BlockMenuItem } from "../editor/block-menu";
import type { SlashItem } from "../editor/slash-menu";

/** What the document gives a part's node views. */
export interface DocumentBlockContext {
  /**
   * The answers of a ui block (`useAnswers`): a React hook, called exactly once
   * per render of the block's UI. Undefined where answers are not collected
   * (`blockId` null: a nested block, or a block without an id yet).
   */
  answersFor(blockId: string | null): UiAnswersSource | undefined;
}

/** The block a block menu opened on. */
export interface BlockMenuTarget {
  node: PMNode;
  pos: number;
}

/** What the static view gives a part's slots. */
export interface StaticSlotsProps {
  /** The element holding the static HTML (its ref is set before the slots' layout effects run). */
  root: { readonly current: HTMLElement | null };
  /** The dressed HTML now in `root` (a new value means new elements). */
  html: string;
  /** The markdown the HTML was made from. */
  markdown: string;
}

export interface DocumentBlockPart {
  id: string;
  /** Draws code blocks of `language` instead of the plain band. */
  codeBlock?: {
    language: string;
    view(ctx: DocumentBlockContext): NodeViewRenderer;
    /**
     * Keyboard shortcuts of the code block node; return false to let the
     * default (and other parts) handle the key.
     */
    keys?: Readonly<Record<string, (editor: Editor) => boolean>>;
  };
  /** Items added to the document body's `/` menu. */
  slashItems?: readonly SlashItem[];
  /**
   * Items for a block's menu, or null when the block is not this part's.
   * `replacesTypes`: the block's "Turn into" items are left out.
   */
  blockMenu?(
    editor: Editor,
    target: BlockMenuTarget,
    nested: boolean,
  ): { items: BlockMenuItem[]; replacesTypes?: boolean } | null;
  /** The static view (before the editor syncs, and read-only bodies). */
  staticBody?: {
    /** Reshapes the static HTML (detached, after code blocks got their band). */
    dress(root: HTMLElement): void;
    /** A press inside the static HTML; true when it was this part's. */
    click?(root: HTMLElement, target: Element): boolean;
    /** Drawn next to the static HTML (portals into what `dress` made). */
    Slots?: ComponentType<StaticSlotsProps>;
  };
  /**
   * What the switch from the static view to the synced editor waits for.
   * First, at most `staticWaitMs`, until nothing in the static view is
   * `pending`; then, at most `handoverMs`, until the editor has `caughtUp`
   * with the static view. Checked again whenever `event` reaches the document.
   */
  handover?: {
    event: string;
    pending(staticRoot: Element | null): boolean;
    caughtUp(staticRoot: Element | null, editorRoot: Element | null): boolean;
    staticWaitMs: number;
    handoverMs: number;
  };
}

const NO_PARTS: readonly DocumentBlockPart[] = [];

export const DocumentBlockPartsContext =
  createContext<readonly DocumentBlockPart[]>(NO_PARTS);

/** The parts of the document body drawn here (none outside a document screen). */
export function useDocumentBlockParts(): readonly DocumentBlockPart[] {
  return useContext(DocumentBlockPartsContext);
}
